import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X, Send, Loader2, Sparkles, LayoutGrid, Gauge, GanttChartSquare,
  CheckSquare, IdCard, MessageSquare, BookOpen, Building2, Bot, BarChart3, Wand2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import maestroHands from "@/assets/maestro-hands.png";
import { TOUR_SHOTS } from "@/lib/tourShots";

const PLAN_CHOICES = [
  ["Waterfall", "Waterfall"],
  ["Agile", "Agile (Scrum)"],
  ["Hybrid", "Hybrid (Waterfall + Agile)"],
  ["IO Map", "IO Map (Input → Output)"],
  ["Event", "Event Planning"],
  ["Something else…", null],
];
const INDUSTRY_CHIPS = ["Technology / SaaS", "Construction", "Healthcare", "Finance", "Government", "Education", "Events & Media"];

const SLIDES = {
  layers: { icon: LayoutGrid, title: "Three layers, one command center", body: "The toggle at the top of every page moves you between layers:", points: ["Project — the working home of a single plan", "Portfolio — every project rolled up for leaders", "PMO — your standards, templates and knowledge base"] },
  dashboard: { icon: Gauge, title: "Your dashboard", body: "Your whole stack at a glance — and everything is clickable:", points: ["Week at a glance + meetings", "Project health, progress and automation savings", "Overload alerts when someone's task load runs heavy"] },
  plan: { icon: GanttChartSquare, title: "Plan menu — build & visualize", body: "Home base for the plan itself, with visual tools to illustrate it every way:", points: ["WBS spreadsheet, Gantt, Roadmap with critical path", "Visual Map network diagram + Board", "IO Flow — every phase gets an Input, Tasks and an Output milestone"] },
  wizard: { icon: Wand2, title: "Plan Wizard — one date in, a full plan out", body: "Racing a deadline? Skip the blank page entirely:", points: ["Give it a start date — it builds the whole plan forward, tasks, durations and dependencies included", "Or give it your deadline — it works backward, tells you when work must start, and compresses the plan if time is tight", "Save any project as a reusable template and pre-assign stage owners as you build"] },
  track: { icon: CheckSquare, title: "Track menu — keep it honest", body: "Game changers that keep the plan truthful without chasing people:", points: ["True-Ups — quick % check-ins that update the plan instantly", "Status Report generator — executive-ready in one click", "Audit tool, Reports, Budget and Baseline compare"] },
  people: { icon: IdCard, title: "People — meet Resource Cards", body: "A uniquely efficient way to track and organize each person's work:", points: ["One living card per person: tasks, action items & true-ups across projects", "Email it in one click — their quick status reply feeds the plan instantly", "Workload heat, nudges with a PDF copy, Monday auto-nudge, resource velocity"] },
  communicate: { icon: MessageSquare, title: "Communicate menu", body: "Everything the team says and decides, in one place:", points: ["Orchestra Pit — your team collaboration space", "Meeting Developer — agendas, minutes and prep", "Notebook, Action Item & Decision registries, SWOT and decision tools"] },
  library: { icon: BookOpen, title: "Library", body: "Templates, lessons learned and knowledge resources ready to reuse:", points: ["Start projects from proven templates", "Capture lessons so every project gets smarter"] },
  pmo: { icon: Building2, title: "PMO layer — your knowledge base", body: "Where your practice lives — the magic in one place:", points: ["Standards, processes and reusable templates", "Registries, audit checklists and lessons learned", "Consistency across every project you run"] },
  maestro: { icon: Bot, title: "Maestro — your AI partner", body: "I'm with you everywhere. Ask for briefings, plan drafts, risk analysis, forecasts:", points: ["Auto Pilot — scheduled check-ins update progress for you", "IO Flow suggestions and one-click fixes", "Stop me anytime and ask a question — like right now, below"] },
  portfolio: { icon: BarChart3, title: "Portfolio layer — for leaders", body: "Every project rolled up for programs and PMO leadership:", points: ["Executive dashboard, stats and program grouping", "Cross-project resource and assignment views", "Portfolio-level status reporting"] },
};
const tourFor = (role) => {
  const base = ["layers", "dashboard", "plan", "wizard", "track", "people", "communicate", "library", "pmo", "maestro"];
  return role === "Project Manager" ? base : [...base.slice(0, -1), "portfolio", "maestro"];
};

const AccentBars = () => (
  <span className="inline-flex items-stretch gap-1.5 mr-5 shrink-0" aria-hidden>
    <span className="w-3 bg-[#0d1e4c]" style={{ height: 44 }} />
    <span className="w-3 bg-[#2f6bff]" style={{ height: 44 }} />
    <span className="w-3 bg-slate-200" style={{ height: 44 }} />
  </span>
);

const BrandPanel = ({ shot }) => (
  <div className="hidden md:flex flex-col items-center shrink-0" data-testid="onboard-brand-panel">
    <div className="bg-white border border-slate-100 p-4 lg:p-5 shadow-[0_20px_60px_rgba(13,30,76,0.18)]">
      {shot ? (
        <div className="w-[380px] lg:w-[520px]">
          <img key={shot[0]} src={shot[0]} alt={shot[1]} className="w-full h-auto border border-slate-200 select-none animate-in fade-in duration-500" draggable="false" data-testid="onboard-tour-shot" />
          <p className="text-center text-[11px] lg:text-xs font-bold tracking-wide text-[#2f6bff] mt-3" data-testid="onboard-tour-caption">{shot[1]}</p>
        </div>
      ) : (
        <div className="bg-gradient-to-b from-[#12275e] to-[#081130] px-7 pt-7 pb-6 flex flex-col items-center w-[320px] lg:w-[430px]">
          <img src={maestroHands} alt="Project Management Orchestrator" className="w-full h-auto select-none" draggable="false" data-testid="onboard-brand-hands" />
          <p className="font-display font-extrabold text-white text-lg lg:text-2xl mt-2 whitespace-nowrap" data-testid="onboard-brand-wordmark">
            Project Management <span className="bg-gradient-to-r from-[#7ea6ff] to-[#4d85ff] bg-clip-text text-transparent">Orchestrator</span>
          </p>
          <p className="text-[10px] lg:text-xs tracking-[0.32em] font-bold text-[#7ea6ff] mt-2">PLAN · MANAGE · ORCHESTRATE</p>
        </div>
      )}
    </div>
    <div className="h-1 w-[94%] bg-[#2f6bff] mt-5 rounded-full" />
  </div>
);

const SlideCard = ({ slide, index, total }) => {
  const Icon = slide.icon;
  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm p-5 max-w-xl" data-testid={`onboard-slide-${index}`}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="h-9 w-9 rounded-lg bg-[#2f6bff]/10 text-[#2f6bff] flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
        <p className="font-display font-extrabold text-slate-900 text-lg">{slide.title}</p>
        <span className="ml-auto text-[11px] font-bold text-[#2f6bff] tabular-nums">{index + 1}/{total}</span>
      </div>
      <p className="text-sm text-slate-500 mb-2.5">{slide.body}</p>
      <ul className="space-y-1.5">
        {slide.points.map((p, i) => (
          <li key={i} className="text-[15px] text-slate-800 flex gap-2"><Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /><span>{p}</span></li>
        ))}
      </ul>
    </div>
  );
};

export const MaestroOnboarding = ({ firstName, replay = false, onClose }) => {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState("welcome");
  const [notes, setNotes] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState("");
  const [slideIdx, setSlideIdx] = useState(0);
  const [shotOverrides, setShotOverrides] = useState({});
  const profile = useRef({});

  useEffect(() => {
    api.get("/tour-shots").then(({ data }) => setShotOverrides(data || {})).catch(() => {});
  }, []);

  const go = (next, ...newNotes) => { setNotes(newNotes.filter(Boolean)); setAnswer(""); setStep(next); };

  const saveProfile = async (patch) => {
    try { await api.put("/me/onboarding", patch); } catch (e) { console.debug("onboarding save failed", e); }
  };

  const close = async (openCreate = false) => {
    if (!replay) {
      try { await api.post("/auth/tour-done"); await api.post("/me/welcome"); refreshUser(); } catch (e) { console.debug(e); }
    }
    onClose?.();
    if (openCreate) {
      const p = profile.current;
      localStorage.setItem("pmo_onboarding_prefill", JSON.stringify({ planType: p.planMethodology || "", industry: p.industry || "" }));
      nav("/dashboard?new=1");
    }
  };

  const pickRole = (role) => {
    profile.current.role = role;
    saveProfile({ role_profile: role });
    go("plan", role === "Project Manager" ? "Great — I'll focus your tour on the Project and PMO layers." : "Wonderful — you'll get the full tour: Portfolio, Project and PMO layers.");
  };

  const pickPlan = (label, methodology) => {
    if (!methodology) { go("planCustom"); return; }
    profile.current.planLabel = label; profile.current.planMethodology = methodology;
    saveProfile({ plan_type: label });
    go("industry", `${label} — nice choice.`);
  };

  const submitPlanCustom = () => {
    const t = freeText.trim(); if (!t) return;
    setFreeText("");
    profile.current.planLabel = t; profile.current.planMethodology = "";
    saveProfile({ plan_type: t });
    go("industry", "Got it — I can draft a plan for just about anything.");
  };

  const pickIndustry = (ind) => {
    setFreeText("");
    profile.current.industry = ind;
    saveProfile({ industry: ind });
    go("autopilot", `${ind} — perfect.`);
  };

  const explainAutopilot = () => {
    setNotes(["Auto Pilot is me working in the background: on a schedule you pick, I email each task owner a quick check-in. They reply with a percent or a click, and the plan updates itself — no chasing. I also send digests and reminders, and flag anything drifting off track. You stay in control and can pause it anytime."]);
  };

  const pickAutopilot = (yes) => {
    profile.current.autopilot = yes;
    saveProfile({ autopilot_optin: yes });
    go("guidance", yes ? "Excellent — look for the Auto Pilot switch on your dashboard once your project exists." : "No problem — it's always one click away on your dashboard.");
  };

  const pickGuidance = (yes) => {
    saveProfile({ proactive_guidance: yes });
    go("tourOffer", yes ? "Deal — I'll only chime in when it's genuinely useful." : "Understood — I'll stay quiet. You can turn tips on later from the Meet Maestro button up top.");
  };

  const startTour = () => { setSlideIdx(0); go("tour", "Ask me anything below while we walk through — you can stop the tour anytime."); };

  const nextSlide = () => {
    const keys = tourFor(profile.current.role);
    const n = slideIdx + 1;
    setAnswer("");
    if (n >= keys.length) { go("finish"); return; }
    setSlideIdx(n);
  };

  const ask = async () => {
    const q = question.trim(); if (!q || asking) return;
    setQuestion(""); setAsking(true); setAnswer("");
    try {
      const keys = tourFor(profile.current.role);
      const ctx = step === "tour" ? `slide "${SLIDES[keys[slideIdx]]?.title}"` : "wrap-up";
      const { data } = await api.post("/onboarding/ask", { question: q, context: ctx });
      setAnswer(data.answer || "Hmm, I lost my train of thought — try me again?");
    } catch (e) {
      setAnswer(e.response?.data?.detail || "I couldn't reach my brain just now — please try again in a moment.");
    } finally { setAsking(false); }
  };

  const STEP_COPY = {
    welcome: { headline: <>Hello <span className="text-[#2f6bff]">{firstName || "there"}!</span>&nbsp; Welcome to the Project Management Orchestrator!!</>, subline: "I am your Project Maestro — I can help you get started. You can stop me at any time and ask questions." },
    role: { headline: <>Tell me about your current role!!</>, subline: "I am a…" },
    plan: { headline: <>What kind of plan would you like to build today?</>, subline: "I'd like to build a…" },
    planCustom: { headline: <>Tell me in your own words…</>, subline: "I'd like to build a plan for…" },
    industry: { headline: <>What industry do you work in?</>, subline: "I currently work in the…" },
    autopilot: { headline: <>Would you like me to automate your plan progress updates?</>, subline: "With Auto Pilot, I…" },
    guidance: { headline: <>Want me to proactively point you to features that can help as you work?</>, subline: "You can turn this off anytime…" },
    tourOffer: { headline: <>Would you like for me to…</>, subline: null },
    tour: { headline: null, subline: null },
    finish: { headline: <>That's the tour{firstName ? <>, <span className="text-[#2f6bff]">{firstName}</span></> : ""}! 🎉</>, subline: profile.current.planLabel ? `I've saved your profile and I'm ready to draft your ${profile.current.planLabel} plan.` : "I've saved your profile — let's get building." },
  };

  const chips = (() => {
    switch (step) {
      case "welcome": return [["Let's get started →", () => go("role")], ["I'll explore on my own", () => close(false)]];
      case "role": return [["PMO Leader", () => pickRole("PMO Leader")], ["Program Manager", () => pickRole("Program Manager")], ["Project Manager", () => pickRole("Project Manager")]];
      case "plan": return PLAN_CHOICES.map(([label, m]) => [label, () => pickPlan(label, m)]);
      case "industry": return INDUSTRY_CHIPS.map((i) => [i, () => pickIndustry(i)]);
      case "autopilot": return [["Yes, sounds great", () => pickAutopilot(true)], ["What does that mean?", explainAutopilot], ["Not now", () => pickAutopilot(false)]];
      case "guidance": return [["Yes, guide me", () => pickGuidance(true)], ["No thanks", () => pickGuidance(false)]];
      case "tourOffer": return [["Yes, show me around", startTour], ["Skip the tour", () => go("finish")]];
      case "tour": return [[slideIdx + 1 >= tourFor(profile.current.role).length ? "Finish the tour" : "Next →", nextSlide]];
      case "finish": return [["✨ Create my first project", () => close(true)], ["I'll explore on my own", () => close(false)]];
      default: return [];
    }
  })();

  const showFreeText = step === "planCustom" || step === "industry";
  const showQuestion = step === "tour" || step === "finish";
  const copy = STEP_COPY[step] || {};
  const keys = tourFor(profile.current.role);

  return (
    <div className="fixed inset-0 z-[130] bg-white overflow-y-auto" data-testid="maestro-onboarding">
      <button type="button" onClick={() => close(false)} className="fixed top-5 right-6 z-10 text-slate-400 hover:text-slate-700 transition-colors" title="Stop Maestro (you can replay anytime from the header)" data-testid="onboard-close">
        <X className="h-6 w-6" />
      </button>

      <div key={step === "tour" ? `tour-${slideIdx}` : step} className="min-h-full max-w-[1280px] mx-auto px-6 sm:px-10 py-10 flex flex-col md:flex-row items-center gap-10 lg:gap-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Left — question / content */}
        <div className="flex-1 w-full max-w-2xl">
          {notes.length > 0 && (
            <p className="text-slate-500 text-base lg:text-lg mb-6" data-testid="onboard-note">{notes[notes.length - 1]}</p>
          )}

          {step === "tour" ? (
            <div className="space-y-5">
              <div className="flex items-start">
                <AccentBars />
                <h1 className="font-display text-2xl lg:text-4xl font-bold text-slate-900 leading-tight">Let me show you around…</h1>
              </div>
              <SlideCard slide={SLIDES[keys[slideIdx]]} index={slideIdx} total={keys.length} />
            </div>
          ) : (
            <>
              <div className="flex items-start">
                {step !== "welcome" && <AccentBars />}
                <h1 className="font-display text-3xl lg:text-[44px] font-bold text-slate-900 leading-[1.15]" data-testid="onboard-headline">{copy.headline}</h1>
              </div>
              {copy.subline && <p className="text-[#2f6bff] text-xl lg:text-2xl mt-8 ml-1" data-testid="onboard-subline">{copy.subline}</p>}
            </>
          )}

          {chips.length > 0 && (
            <div className="mt-8 flex flex-col items-start gap-4" data-testid="onboard-chips">
              {chips.map(([label, fn]) => (
                <button key={label} type="button" onClick={fn}
                  className="text-left text-[#2f6bff] text-lg lg:text-2xl font-medium hover:translate-x-1.5 hover:text-[#1d4fd8] transition-all duration-150"
                  data-testid={`onboard-chip-${label.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {showFreeText && (
            <form className="mt-8 flex gap-2 max-w-md" onSubmit={(e) => { e.preventDefault(); step === "planCustom" ? submitPlanCustom() : (freeText.trim() && pickIndustry(freeText.trim())); }}>
              <Input value={freeText} onChange={(e) => setFreeText(e.target.value)} autoFocus={step === "planCustom"}
                className="h-11 border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#2f6bff]"
                placeholder={step === "planCustom" ? "Describe your plan…" : "Or type your industry…"} data-testid="onboard-free-input" />
              <Button type="submit" size="icon" className="h-11 w-11 bg-[#2f6bff] hover:bg-[#2558d6]" disabled={!freeText.trim()} data-testid="onboard-free-send"><Send className="h-4 w-4" /></Button>
            </form>
          )}

          {showQuestion && (
            <div className="mt-8 max-w-xl">
              {(asking || answer) && (
                <div className="mb-4 border-l-4 border-[#2f6bff] bg-[#2f6bff]/5 rounded-r-lg px-4 py-3 text-slate-700 text-[15px] leading-relaxed" data-testid="onboard-answer">
                  {asking ? <Loader2 className="h-4 w-4 animate-spin text-[#2f6bff]" /> : answer}
                </div>
              )}
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
                <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                  className="h-11 border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#2f6bff]"
                  placeholder="Ask me anything about what you're seeing…" data-testid="onboard-question-input" />
                <Button type="submit" size="icon" variant="outline" className="h-11 w-11 border-slate-300 text-[#2f6bff] hover:bg-[#2f6bff]/5 hover:text-[#2f6bff]" disabled={!question.trim() || asking} data-testid="onboard-question-send">
                  {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Right — brand panel / tour screenshots */}
        <BrandPanel shot={step === "tour" ? [shotOverrides[keys[slideIdx]] || TOUR_SHOTS[keys[slideIdx]][0], TOUR_SHOTS[keys[slideIdx]][1]] : null} />
      </div>
    </div>
  );
};

export default MaestroOnboarding;
